import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { ArrowLeft, Save } from "lucide-react";
import { ExerciseDemo } from "@/components/ExerciseDemo";

export default function WorkoutPage() {
  const { id } = useParams();
  const { user } = useAuth();
  const [w, setW] = useState(null);
  const [logs, setLogs] = useState({});
  const [history, setHistory] = useState({});

  useEffect(() => {
    api.get(`/workouts/${id}`).then((r) => setW(r.data));
    api.get(`/workouts/${id}/history`).then((r) => setHistory(r.data.history)).catch(() => {});
  }, [id]);

  const save = async () => {
    const exercises = w.exercises.map((ex, i) => ({
      name: ex.name,
      target_sets: ex.sets,
      target_reps: ex.reps,
      weight: logs[i]?.weight || "",
      done_sets: logs[i]?.done_sets || 0,
    }));
    await api.post("/workouts/log", { workout_id: id, exercises, duration_min: w.duration_min });
    const h = await api.get(`/workouts/${id}/history`);
    setHistory(h.data.history);
    alert("Workout logged. Proof secured.");
  };

  if (!w) return <div className="p-10 text-zinc-500">Loading…</div>;

  return (
    <div className="fade-up">
      <Link to="/program" className="btn-ghost inline-flex items-center gap-2 pl-0" data-testid="back-workout">
        <ArrowLeft size={14}/> Back
      </Link>
      <p className="eyebrow mt-4" style={{ color: "#FF0055" }}>{w.category} · {w.difficulty}</p>
      <h1 className="font-display font-black text-3xl md:text-5xl mt-2 brand-underline uppercase">{w.title}</h1>
      <p className="text-zinc-400 mt-6">{w.focus} · {w.duration_min}m</p>

      <div className="mt-8 divide-y divide-zinc-800 card p-0">
        {w.exercises.map((ex, i) => (
          <div key={i} className="p-5 grid grid-cols-1 md:grid-cols-[160px_1fr_1fr_1fr] gap-5 items-center" data-testid={`exercise-row-${i}`}>
            <div className="max-w-[220px] md:max-w-none">
              <ExerciseDemo workoutId={id} index={i} exercise={ex} isAdmin={user?.role === "admin"} onUpdated={setW} />
            </div>
            <div>
              <p className="font-display font-bold uppercase">{ex.name}</p>
              <p className="text-xs text-zinc-500 mt-1">{ex.notes}</p>
              <p className="text-xs mt-2 uppercase tracking-widest" style={{ color: "#FF0055" }}>{ex.sets}×{ex.reps}</p>
              {history[ex.name] && (
                <div className="mt-3 flex flex-wrap gap-2" data-testid={`ex-history-${i}`}>
                  <span className="text-[10px] uppercase tracking-widest px-2 py-1" style={{ background: "#141414", border: "1px solid #27272A", color: "#d4d4d8" }} data-testid={`ex-last-${i}`}>
                    Last: {history[ex.name].last.weight > 0 ? `${history[ex.name].last.weight} kg` : "bodyweight"} × {history[ex.name].last.done_sets} sets · {history[ex.name].last.date.slice(5).replace("-", "/")}
                  </span>
                  {history[ex.name].best_weight > 0 && (
                    <span className="text-[10px] uppercase tracking-widest px-2 py-1 font-bold" style={{ background: "rgba(255,0,85,0.12)", border: "1px solid #FF0055", color: "#FF0055" }} data-testid={`ex-best-${i}`}>
                      Best: {history[ex.name].best_weight} kg
                    </span>
                  )}
                </div>
              )}
            </div>
            <div>
              <label className="label">Weight (kg)</label>
              <input className="input" type="number" placeholder="—"
                value={logs[i]?.weight || ""}
                onChange={(e) => setLogs({ ...logs, [i]: { ...logs[i], weight: e.target.value } })}
                data-testid={`ex-weight-${i}`}
              />
            </div>
            <div>
              <label className="label">Sets done</label>
              <input className="input" type="number" placeholder={ex.sets}
                value={logs[i]?.done_sets || ""}
                onChange={(e) => setLogs({ ...logs, [i]: { ...logs[i], done_sets: parseInt(e.target.value) || 0 } })}
              />
            </div>
          </div>
        ))}
      </div>

      <div className="mt-6">
        <button className="btn-primary flex items-center gap-2" onClick={save} data-testid="save-workout">
          <Save size={14}/> Save workout
        </button>
      </div>
    </div>
  );
}
